import React from 'react';
import styled from 'styled-components';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const SpacingCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
`;

const SpacingLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SpacingName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#0F172A')};
`;

const SpacingValue = styled.code`
  font-size: 13px;
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  color: #FA4616;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  padding: 4px 8px;
  border-radius: 4px;
`;

const SpacingVisual = styled.div`
  height: ${(props) => props.size};
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.3)'
      : 'rgba(250, 70, 22, 0.2)'};
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FA4616;
  font-size: 12px;
  font-weight: 600;
`;

const SpacingUsage = styled.div`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 12px;
  line-height: 1.5;
`;

const GridExample = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${(props) => props.gap};
  margin: 24px 0;
`;

const GridItem = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(250, 70, 22, 0.3)' : '#FA4616'};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#FA4616' : '#FA4616')};
  font-size: 14px;
  font-weight: 600;
`;

const Spacing = () => {
  return (
    <div>
      <PageHeader>
        <Title>Spacing</Title>
        <Subtitle>
          Our spacing system uses a consistent scale to create rhythm and
          hierarchy throughout the interface. All spacing values are multiples
          of 4px for pixel-perfect alignment.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Spacing Scale</SectionTitle>
        <SectionDescription>
          Six spacing tokens provide flexibility for all layout needs, from
          tight component spacing to generous page margins.
        </SectionDescription>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Extra Small (xs)</SpacingName>
            <SpacingValue>4px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="4px">4px</SpacingVisual>
          <SpacingUsage>
            Use for: Icon spacing, tight badges, minimal gaps
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Small (sm)</SpacingName>
            <SpacingValue>8px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="8px">8px</SpacingVisual>
          <SpacingUsage>
            Use for: Button padding, form field spacing, compact layouts
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Medium (md)</SpacingName>
            <SpacingValue>16px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="16px">16px</SpacingVisual>
          <SpacingUsage>
            Use for: Card padding, standard gaps, element spacing
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Large (lg)</SpacingName>
            <SpacingValue>24px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="24px">24px</SpacingVisual>
          <SpacingUsage>
            Use for: Section spacing, comfortable margins, between components
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Extra Large (xl)</SpacingName>
            <SpacingValue>32px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="32px">32px</SpacingVisual>
          <SpacingUsage>
            Use for: Page padding, large gaps, major sections
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          <SpacingLabel>
            <SpacingName>Extra Extra Large (xxl)</SpacingName>
            <SpacingValue>48px</SpacingValue>
          </SpacingLabel>
          <SpacingVisual size="48px">48px</SpacingVisual>
          <SpacingUsage>
            Use for: Page headers, hero sections, major visual breaks
          </SpacingUsage>
        </SpacingCard>

        <CodeBlock>
{`// Access spacing from theme
const Container = styled.div\`
  padding: \${props => props.theme.spacing.md};
  gap: \${props => props.theme.spacing.lg};
\`;

// Or use specific values
padding: \${props => props.theme.spacing.xl}; // 32px
margin: \${props => props.theme.spacing.sm}; // 8px`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Grid Gap Examples</SectionTitle>
        <SectionDescription>
          See how different spacing values affect grid layouts.
        </SectionDescription>

        <ComponentDemo label="Gap: xs (4px)" compact>
          <GridExample gap="4px">
            <GridItem>Item 1</GridItem>
            <GridItem>Item 2</GridItem>
            <GridItem>Item 3</GridItem>
          </GridExample>
        </ComponentDemo>

        <ComponentDemo label="Gap: sm (8px)" compact>
          <GridExample gap="8px">
            <GridItem>Item 1</GridItem>
            <GridItem>Item 2</GridItem>
            <GridItem>Item 3</GridItem>
          </GridExample>
        </ComponentDemo>

        <ComponentDemo label="Gap: md (16px)" compact>
          <GridExample gap="16px">
            <GridItem>Item 1</GridItem>
            <GridItem>Item 2</GridItem>
            <GridItem>Item 3</GridItem>
          </GridExample>
        </ComponentDemo>

        <ComponentDemo label="Gap: lg (24px)" compact>
          <GridExample gap="24px">
            <GridItem>Item 1</GridItem>
            <GridItem>Item 2</GridItem>
            <GridItem>Item 3</GridItem>
          </GridExample>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Patterns</SectionTitle>
        <SectionDescription>
          Standard spacing patterns used throughout the application.
        </SectionDescription>

        <CodeBlock>
{`// Card with standard spacing
const Card = styled.div\`
  padding: \${props => props.theme.spacing.lg}; // 24px
  gap: \${props => props.theme.spacing.md}; // 16px
  border-radius: 8px;
\`;

// Compact card
const CompactCard = styled.div\`
  padding: \${props => props.theme.spacing.md}; // 16px
  gap: \${props => props.theme.spacing.sm}; // 8px
  border-radius: 8px;
\`;

// Page container
const PageContainer = styled.div\`
  padding: \${props => props.theme.spacing.xl} \${props => props.theme.spacing.lg};
  // 32px top/bottom, 24px left/right
\`;

// Section spacing
const Section = styled.section\`
  margin-bottom: \${props => props.theme.spacing.xxl}; // 48px

  @media (max-width: 768px) {
    margin-bottom: \${props => props.theme.spacing.lg}; // 24px on mobile
  }
\`;

// Grid with responsive gaps
const Grid = styled.div\`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: \${props => props.theme.spacing.lg}; // 24px

  @media (max-width: 768px) {
    gap: \${props => props.theme.spacing.md}; // 16px on mobile
  }
\`;

// Stack with consistent spacing
const Stack = styled.div\`
  display: flex;
  flex-direction: column;
  gap: \${props => props.theme.spacing.md}; // 16px between items

  > * + * {
    margin-top: 0; // Reset margins, use gap instead
  }
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Responsive Spacing</SectionTitle>
        <SectionDescription>
          Adjust spacing for different screen sizes to optimize mobile
          experiences.
        </SectionDescription>

        <CodeBlock>
{`// Reduce spacing on mobile
const Container = styled.div\`
  padding: \${props => props.theme.spacing.xl}; // 32px on desktop

  @media (max-width: 768px) {
    padding: \${props => props.theme.spacing.md}; // 16px on mobile
  }
\`;

// Increase gap on larger screens
const Grid = styled.div\`
  display: grid;
  gap: \${props => props.theme.spacing.md}; // 16px base

  @media (min-width: 1024px) {
    gap: \${props => props.theme.spacing.xl}; // 32px on large screens
  }
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <SectionDescription>
          Guidelines for using spacing effectively in your components.
        </SectionDescription>

        <SpacingCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <SpacingName style={{ marginBottom: '12px', display: 'block' }}>
            ✅ Do
          </SpacingName>
          <SpacingUsage>
            • Use spacing tokens from the theme for consistency
            <br />
            • Reduce spacing on mobile for better space utilization
            <br />
            • Use larger spacing (lg, xl, xxl) for major visual breaks
            <br />
            • Use smaller spacing (xs, sm, md) within components
            <br />• Maintain consistent spacing patterns throughout your feature
          </SpacingUsage>
        </SpacingCard>

        <SpacingCard>
          {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
          <SpacingName style={{ marginBottom: '12px', display: 'block' }}>
            ❌ Don't
          </SpacingName>
          <SpacingUsage>
            • Don't use arbitrary pixel values - stick to the spacing scale
            <br />
            • Don't mix margin and padding for the same spacing purpose
            <br />
            • Don't use excessive spacing that creates awkward gaps
            <br />• Don't forget to test spacing on different screen sizes
          </SpacingUsage>
        </SpacingCard>
      </Section>
    </div>
  );
};

export default Spacing;